/**
 * Avaliador da linguagem de condição no cliente — **só para UX**.
 *
 * Espelha `AvaliadorCondicao` (T8a) e é provado pela fixture `__fixtures__/condicao-paridade.json`,
 * copiada byte a byte do backend. A decisão final é sempre do servidor, que revalida no envio.
 *
 * Nunca lança por causa dos dados: valor de tipo inesperado torna a condição atômica falsa.
 */
import {
  Condicao,
  CondicaoAtomica,
  ContextoCondicao,
  Fonte,
  Grupo,
  Operador,
  ValorCondicao,
  ValorContexto,
  ehGrupo,
  ehGrupoTodos,
  itensDoGrupo,
} from './tipos'
import { ausente, normalizar } from './valores'

export const avaliarCondicao = (condicao: Condicao, contexto: ContextoCondicao): boolean => {
  if (ehGrupo(condicao)) return avaliarGrupo(condicao, contexto)
  return avaliarAtomica(condicao as CondicaoAtomica, contexto)
}

const avaliarGrupo = (grupo: Grupo, contexto: ContextoCondicao): boolean => {
  const itens = itensDoGrupo(grupo)
  return ehGrupoTodos(grupo)
    ? itens.every((item) => avaliarCondicao(item, contexto))
    : itens.some((item) => avaliarCondicao(item, contexto))
}

const avaliarAtomica = (atomica: CondicaoAtomica, contexto: ContextoCondicao): boolean => {
  const observado = resolver(atomica.fonte, atomica.ref, contexto)
  const semValor = ausente(observado)

  if (atomica.op === 'existe') return !semValor
  if (atomica.op === 'nao_existe') return semValor
  if (semValor || observado == null) return false

  switch (observado.tipo) {
    case 'numero':
      return compararNumero(observado.v, atomica.op, atomica.valor)
    case 'texto':
      return compararTextos([observado.v], atomica.op, atomica.valor)
    default:
      return compararTextos(observado.v, atomica.op, atomica.valor)
  }
}

const resolver = (fonte: Fonte, ref: string, contexto: ContextoCondicao): ValorContexto | null => {
  switch (fonte) {
    case 'campo':
      return contexto.respostas[ref] ?? null
    case 'variavel':
      return contexto.variaveis[ref] ?? null
    case 'sinal': {
      const sinal = contexto.sinais[ref]
      return typeof sinal === 'number' ? { tipo: 'numero', v: sinal } : null
    }
    default:
      return null
  }
}

const todosNumeros = (valor: ValorCondicao): valor is number[] =>
  Array.isArray(valor) && (valor as unknown[]).every((item) => typeof item === 'number')

const todosTextos = (valor: ValorCondicao): valor is string[] =>
  Array.isArray(valor) && (valor as unknown[]).every((item) => typeof item === 'string')

const compararNumero = (
  observado: number,
  op: Operador,
  literal: ValorCondicao | undefined
): boolean => {
  if (literal === undefined) return false
  if (op === 'entre') {
    return (
      todosNumeros(literal) &&
      literal.length === 2 &&
      observado >= literal[0] &&
      observado <= literal[1]
    )
  }
  if (op === 'em') {
    return todosNumeros(literal) && literal.some((item: number) => item === observado)
  }
  if (typeof literal !== 'number') return false
  switch (op) {
    case '=':
      return observado === literal
    case '!=':
      return observado !== literal
    case '>':
      return observado > literal
    case '>=':
      return observado >= literal
    case '<':
      return observado < literal
    case '<=':
      return observado <= literal
    default:
      return false
  }
}

const compararTextos = (
  observados: string[],
  op: Operador,
  literal: ValorCondicao | undefined
): boolean => {
  if (literal === undefined) return false
  const normalizados = observados.map(normalizar)
  switch (op) {
    case '=':
      return typeof literal === 'string' && normalizados.includes(normalizar(literal))
    case '!=':
      return typeof literal === 'string' && !normalizados.includes(normalizar(literal))
    case 'em':
      return (
        todosTextos(literal) &&
        literal.map(normalizar).some((item: string) => normalizados.includes(item))
      )
    default:
      return false
  }
}
